/**
 * WebSocket React Hooks
 *
 * 컴포넌트에서 WebSocket 이벤트를 쉽게 구독하기 위한 훅
 */

import { useEffect, useCallback, useRef } from 'react';
import { useWebSocketStore } from '../stores/websocket';
import { ServerEvents } from '../lib/websocket';
import {
  InspectionEventPayload,
  PhotoAnalysisProgressPayload,
  ApprovalEventPayload,
  MemberEventPayload,
  ReportEventPayload,
  SiteMemberEventPayload,
} from '../types/websocket';

/**
 * WebSocket 이벤트 구독 훅
 *
 * @param event - 구독할 이벤트
 * @param handler - 이벤트 핸들러
 * @param deps - 의존성 배열 (핸들러가 변경되는 조건)
 *
 * @example
 * ```tsx
 * useWebSocketEvent(ServerEvents.INSPECTION_CREATED, (data) => {
 *   console.log('New inspection:', data);
 *   refetch();
 * });
 * ```
 */
export function useWebSocketEvent<T = unknown>(
  event: `${ServerEvents}`,
  handler: (data: T) => void,
  deps: React.DependencyList = []
): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);
  const handlerRef = useRef(handler);

  // 핸들러 참조 업데이트
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const unsubscribe = subscribe<T>(event, (data) => {
      handlerRef.current(data);
    });

    return unsubscribe;
  }, [event, subscribe, ...deps]);
}

/**
 * 여러 WebSocket 이벤트 구독 훅
 *
 * @param events - 구독할 이벤트 배열
 * @param handler - 이벤트 핸들러 (이벤트 이름과 데이터를 받음)
 *
 * @example
 * ```tsx
 * useWebSocketEvents(
 *   [ServerEvents.INSPECTION_CREATED, ServerEvents.INSPECTION_UPDATED],
 *   (event, data) => {
 *     console.log(`${event}:`, data);
 *     refetch();
 *   }
 * );
 * ```
 */
export function useWebSocketEvents<T = unknown>(
  events: `${ServerEvents}`[],
  handler: (event: `${ServerEvents}`, data: T) => void
): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    const unsubscribes = events.map((event) =>
      subscribe<T>(event, (data) => {
        handlerRef.current(event, data);
      })
    );

    return () => {
      unsubscribes.forEach((unsubscribe) => unsubscribe());
    };
  }, [events.join(','), subscribe]);
}

/**
 * 점검 관련 이벤트 구독 훅
 *
 * @example
 * ```tsx
 * useInspectionEvents({
 *   onCreated: (data) => refetch(),
 *   onUpdated: (data) => refetch(),
 *   onDeleted: (data) => refetch(),
 * });
 * ```
 */
interface InspectionEventHandlers {
  onCreated?: (data: InspectionEventPayload) => void;
  onUpdated?: (data: InspectionEventPayload) => void;
  onDeleted?: (data: InspectionEventPayload) => void;
  onStatusChanged?: (data: InspectionEventPayload) => void;
}

export function useInspectionEvents(handlers: InspectionEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onCreated) {
      unsubscribes.push(
        subscribe<InspectionEventPayload>(ServerEvents.INSPECTION_CREATED, handlers.onCreated)
      );
    }
    if (handlers.onUpdated) {
      unsubscribes.push(
        subscribe<InspectionEventPayload>(ServerEvents.INSPECTION_UPDATED, handlers.onUpdated)
      );
    }
    if (handlers.onDeleted) {
      unsubscribes.push(
        subscribe<InspectionEventPayload>(ServerEvents.INSPECTION_DELETED, handlers.onDeleted)
      );
    }
    if (handlers.onStatusChanged) {
      unsubscribes.push(
        subscribe<InspectionEventPayload>(ServerEvents.INSPECTION_STATUS_CHANGED, handlers.onStatusChanged)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

/**
 * 사진 분석 이벤트 구독 훅
 */
interface PhotoAnalysisEventHandlers {
  onStarted?: (data: PhotoAnalysisProgressPayload) => void;
  onProgress?: (data: PhotoAnalysisProgressPayload) => void;
  onCompleted?: (data: PhotoAnalysisProgressPayload) => void;
  onFailed?: (data: PhotoAnalysisProgressPayload) => void;
}

export function usePhotoAnalysisEvents(handlers: PhotoAnalysisEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onStarted) {
      unsubscribes.push(
        subscribe<PhotoAnalysisProgressPayload>(ServerEvents.PHOTO_ANALYSIS_STARTED, handlers.onStarted)
      );
    }
    if (handlers.onProgress) {
      unsubscribes.push(
        subscribe<PhotoAnalysisProgressPayload>(ServerEvents.PHOTO_ANALYSIS_PROGRESS, handlers.onProgress)
      );
    }
    if (handlers.onCompleted) {
      unsubscribes.push(
        subscribe<PhotoAnalysisProgressPayload>(ServerEvents.PHOTO_ANALYSIS_COMPLETED, handlers.onCompleted)
      );
    }
    if (handlers.onFailed) {
      unsubscribes.push(
        subscribe<PhotoAnalysisProgressPayload>(ServerEvents.PHOTO_ANALYSIS_FAILED, handlers.onFailed)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

/**
 * 결재 이벤트 구독 훅
 */
interface ApprovalEventHandlers {
  onRequested?: (data: ApprovalEventPayload) => void;
  onSigned?: (data: ApprovalEventPayload) => void;
  onCompleted?: (data: ApprovalEventPayload) => void;
  onRejected?: (data: ApprovalEventPayload) => void;
}

export function useApprovalEvents(handlers: ApprovalEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onRequested) {
      unsubscribes.push(
        subscribe<ApprovalEventPayload>(ServerEvents.APPROVAL_REQUESTED, handlers.onRequested)
      );
    }
    if (handlers.onSigned) {
      unsubscribes.push(
        subscribe<ApprovalEventPayload>(ServerEvents.APPROVAL_SIGNED, handlers.onSigned)
      );
    }
    if (handlers.onCompleted) {
      unsubscribes.push(
        subscribe<ApprovalEventPayload>(ServerEvents.APPROVAL_COMPLETED, handlers.onCompleted)
      );
    }
    if (handlers.onRejected) {
      unsubscribes.push(
        subscribe<ApprovalEventPayload>(ServerEvents.APPROVAL_REJECTED, handlers.onRejected)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

// TBM 이벤트 묶음 구독 훅을 두지 않는다 — 살아 있는 TBM 이벤트는 `TBM_UPDATED`(교육 변경 —
// `emitEducationChanged`)·`TBM_ATTENDEE_SIGNED`(참석자 서명) 둘뿐이고, 소비처는
// `subscribe(ServerEvents.X)`를 직접 건다(`pages/reports/index.tsx`).
// ⚠️ 이벤트 이름이 다시 필요해지면 **구독이 아니라 emit부터** 놓을 것 — 서버가 쏘지 않는 이름을
// enum에 두면 화면이 죽은 구독을 달게 된다.

/**
 * 보고서 이벤트 구독 훅
 */
interface ReportEventHandlers {
  onCreated?: (data: ReportEventPayload) => void;
  onUpdated?: (data: ReportEventPayload) => void;
  onDeleted?: (data: ReportEventPayload) => void;
  onStatusChanged?: (data: ReportEventPayload) => void;
}

export function useReportEvents(handlers: ReportEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onCreated) {
      unsubscribes.push(
        subscribe<ReportEventPayload>(ServerEvents.REPORT_CREATED, handlers.onCreated)
      );
    }
    if (handlers.onUpdated) {
      unsubscribes.push(
        subscribe<ReportEventPayload>(ServerEvents.REPORT_UPDATED, handlers.onUpdated)
      );
    }
    if (handlers.onDeleted) {
      unsubscribes.push(
        subscribe<ReportEventPayload>(ServerEvents.REPORT_DELETED, handlers.onDeleted)
      );
    }
    if (handlers.onStatusChanged) {
      unsubscribes.push(
        subscribe<ReportEventPayload>(ServerEvents.REPORT_STATUS_CHANGED, handlers.onStatusChanged)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

/**
 * 조직 멤버 이벤트 구독 훅
 */
interface MemberEventHandlers {
  onJoined?: (data: MemberEventPayload) => void;
  onLeft?: (data: MemberEventPayload) => void;
  onRoleChanged?: (data: MemberEventPayload) => void;
  onInvited?: (data: MemberEventPayload) => void;
}

export function useMemberEvents(handlers: MemberEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onJoined) {
      unsubscribes.push(
        subscribe<MemberEventPayload>(ServerEvents.MEMBER_JOINED, handlers.onJoined)
      );
    }
    if (handlers.onLeft) {
      unsubscribes.push(
        subscribe<MemberEventPayload>(ServerEvents.MEMBER_LEFT, handlers.onLeft)
      );
    }
    if (handlers.onRoleChanged) {
      unsubscribes.push(
        subscribe<MemberEventPayload>(ServerEvents.MEMBER_ROLE_CHANGED, handlers.onRoleChanged)
      );
    }
    if (handlers.onInvited) {
      unsubscribes.push(
        subscribe<MemberEventPayload>(ServerEvents.MEMBER_INVITED, handlers.onInvited)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

// (`useSiteEvents`는 없다 — 2026-09-18 R15 제거. `SITE_CREATED`·`SITE_DELETED`는 서버가 어디서도
//  emit하지 않았고 이 훅의 소비처도 0이었다. 현장 수정 실시간 반영은 `WebSocketProvider`가
//  `SITE_UPDATED`를 직접 구독한다. 현장 생성·삭제와 현장 하위 리소스·협력업체·그룹·멤버·현장 결재선의
//  변경은 2026-09-19부터 서버가 조직 room에 `ENTITY_CHANGED`로 브로드캐스트하고, `WebSocketProvider`가
//  `lib/entityChanged.ts`의 표대로 캐시를 무효화한다(삭제된 현장 상세를 보던 남은 `/sites`로 이동).
//  행위자 세션의 ACTION_RESULT `site:*` → `['sites']` 무효화는 그대로다.)

/**
 * 현장 멤버 배정 이벤트 구독 훅
 */
interface SiteMemberEventHandlers {
  onAssigned?: (data: SiteMemberEventPayload) => void;
  onUnassigned?: (data: SiteMemberEventPayload) => void;
}

export function useSiteMemberEvents(handlers: SiteMemberEventHandlers): void {
  const subscribe = useWebSocketStore((state) => state.subscribe);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (handlers.onAssigned) {
      unsubscribes.push(
        subscribe<SiteMemberEventPayload>(ServerEvents.SITE_MEMBER_ASSIGNED, handlers.onAssigned)
      );
    }
    if (handlers.onUnassigned) {
      unsubscribes.push(
        subscribe<SiteMemberEventPayload>(ServerEvents.SITE_MEMBER_UNASSIGNED, handlers.onUnassigned)
      );
    }

    return () => {
      unsubscribes.forEach((fn) => fn());
    };
  }, [handlers, subscribe]);
}

/**
 * 데이터 자동 갱신 훅
 *
 * 지정된 이벤트가 발생하면 refetch 함수를 호출합니다.
 *
 * @param events - 감시할 이벤트 배열
 * @param refetch - 데이터 갱신 함수
 * @param filter - 이벤트 필터 함수 (선택)
 *
 * @example
 * ```tsx
 * const { refetch } = useQuery(['inspections', orgId], fetchInspections);
 *
 * useAutoRefresh(
 *   [ServerEvents.INSPECTION_CREATED, ServerEvents.INSPECTION_UPDATED],
 *   refetch,
 *   (data) => data.companyId === orgId
 * );
 * ```
 */
export function useAutoRefresh<T = unknown>(
  events: ServerEvents[],
  refetch: () => void,
  filter?: (data: T) => boolean
): void {
  const refetchRef = useRef(refetch);
  const filterRef = useRef(filter);

  useEffect(() => {
    refetchRef.current = refetch;
    filterRef.current = filter;
  }, [refetch, filter]);

  useWebSocketEvents<T>(events, useCallback((_event, data) => {
    if (!filterRef.current || filterRef.current(data)) {
      refetchRef.current();
    }
  }, []));
}
